import * as models from 'smg/admin/models';
import { HasPerm } from 'smg/admin/session';
import { ShortURL } from 'smg/admin/short';
import axios, { AxiosStatusCode } from 'smg/axios';
import * as clipboard from 'smg/clipboard';
import { ShowError } from 'smg/errors';
import Modal from 'smg/modal';
import { Picture } from 'smg/picture';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat } from 'smg/util';
import { DeleteCart, GetCart } from '../cart/cart';
import * as cartID from '../cart/cartid';
import { DeleteCartItem, RestoreCartItem } from '../cart/cartitems';
import { ApplyAddParamsFromQuery, PromptAddCartItemsToCart, RenameCart, ShareCart, SwitchCart } from '../cart/cartactions';
import { OpenPriceBreakdown } from '../cart/pricebreakdown';
import { ShortProductName } from '../cart/productnames';
import { SourceOrder } from 'smg/cart-items';
import { Relref } from '../hugo';
import i18n from '../i18n';

declare interface myWindow extends Window {
    readonly CloudFront: string;
}
declare const window: myWindow;

const $spinner = document.getElementById('cart-spinner') as HTMLElement;
const $content = document.getElementById('cart-content') as HTMLElement;
const $empty = document.getElementById('cart-empty') as HTMLElement;
const $heading = document.getElementById('cart-heading') as HTMLElement;
const $items = document.getElementById('cart-items') as HTMLElement;
const $subtotal = document.getElementById('summary-subtotal') as HTMLElement;
const $total = document.getElementById('summary-total') as HTMLElement;
const $switcher = document.getElementById('cart-switcher') as HTMLSelectElement;
const $newBtn = document.getElementById('cart-new-btn') as HTMLButtonElement;
const $addBtn = document.getElementById('cart-add-btn') as HTMLButtonElement;
const $saveBtn = document.getElementById('cart-save-btn') as HTMLButtonElement;
const $shareBtn = document.getElementById('cart-share-btn') as HTMLButtonElement;
const $emptyAddBtn = document.getElementById('cart-empty-add-btn') as HTMLButtonElement;

// Fetch the active cart fresh from the API. Mirrors GetCheckoutCart: a 404 (or
// an ordered/inactive cart) forgets the stored id and resolves to undefined so
// the page renders its empty state instead of throwing.
async function getCart(): Promise<models.CartResponse | undefined> {
    const id = cartID.Get();
    if (!id) {
        return undefined;
    }
    return axios.get(`/carts/${id}`, { params: { q: 'active:yes' } }).
        then(r => {
            const resp = models.CartResponse.fromJSON(r.data);
            if (resp.cart.active !== true || resp.cart.ordered === true) {
                void cartID.Forget();
                return undefined;
            }
            return resp;
        }).
        catch(err => {
            if (AxiosStatusCode(err) === 404) {
                void cartID.Forget();
                return undefined;
            }
            ShowError(err);
            throw err;
        });
}

// Per-product piece count — "150 coins", or "100 pins, 50 coins" when mixed —
// summing product line items only so discount/setup lines don't inflate it.
// Falls back to the bare cart quantity for stock/upload items. Mirrors the
// checkout summary's count so both surfaces read identically.
function pieceCount(cartItem: models.CartItem): string {
    const order = SourceOrder(cartItem);
    const productLineItems = cartItem.quote?.quoteLineItems ?? order?.orderLineItems ?? [];
    const productQuantities = new Map<string, { name: string; pluralName: string; quantity: number }>();
    for (const lineItem of productLineItems) {
        const model = lineItem.lineItemModel;
        if (!model?.product || !lineItem.quantity) {
            continue;
        }
        const key = model.id || model.pluralName || model.name || '';
        const entry = productQuantities.get(key);
        if (entry) {
            entry.quantity += lineItem.quantity;
        } else {
            productQuantities.set(key, {
                name: model.networkLineItem?.name || model.name || '',
                pluralName: model.networkLineItem?.pluralName || model.networkLineItem?.name || model.pluralName || model.name || '',
                quantity: lineItem.quantity,
            });
        }
    }
    const pieces = Array.from(productQuantities.values()).
        map(p => `${p.quantity.toLocaleString()} ${ShortProductName(p.quantity === 1 ? p.name : p.pluralName).toLowerCase()}`).
        join(', ');
    if (pieces) {
        return pieces;
    }
    return `${(cartItem.quantity ?? 0).toLocaleString()} ${i18n('pcs')}`;
}

// Proof caption — quotes number their proofs ("Proof 3"); orders carry a proof
// name instead. Matches the customer-site cart (express/ccn pages/cart.ts).
function proofLabel(entry: models.QuoteProof | models.OrderProof): string {
    if (entry instanceof models.QuoteProof) {
        return entry.number ? i18n('proofN').replace('%s', String(entry.number)) : i18n('proof');
    }
    return entry.name || i18n('proof');
}

// Sets a link's text and appends the trailing external-link icon (opens in a new
// tab). Mirrors the checkout summary's appendLinkLabel.
function appendLinkLabel($link: HTMLAnchorElement, label: string) {
    $link.textContent = label;
    const $icon = document.createElement('i');
    $icon.className = 'fas fa-external-link-alt ms-1';
    $icon.style.fontSize = '.8em';
    $link.appendChild($icon);
}

function renderItem(cartItem: models.CartItem): HTMLElement {
    const $el = template.Clone('#cart-item-template').firstElementChild as HTMLElement;
    const quote = cartItem.quote;
    const order = SourceOrder(cartItem);

    const productLineItems = quote?.quoteLineItems ?? order?.orderLineItems ?? [];
    const firstLineItem = productLineItems[0];
    // Reorder (order-backed) line items leave the custom lineItem/type columns
    // empty — the canonical name lives on the joined catalog models — so fall
    // back to those (brand override first, then base), mirroring the pieceCount
    // resolution. Size falls back to LineItemTypeSize.getName() (its _Name, else
    // readable width×height).
    const productName = firstLineItem?.lineItem || firstLineItem?.lineItemModel?.networkLineItem?.name || firstLineItem?.lineItemModel?.name;
    const productType = firstLineItem?.type || firstLineItem?.lineItemType?.networkLineItemType?.name || firstLineItem?.lineItemType?.name;
    const productSize = firstLineItem?.size || firstLineItem?.lineItemTypeSize?.getName();
    const parts = [productName, productType, productSize].filter(Boolean);

    const $serial = $el.querySelector('.cart-item-serial') as HTMLAnchorElement;
    const $serialCode = $serial.querySelector('code') as HTMLElement;
    if (quote) {
        $serialCode.textContent = `#${quote.serial ?? ''}`;
        $serial.href = quote.iapURL();
        if (cartItem.order) {
            const $reorderOf = document.createElement('span');
            $reorderOf.className = 'small text-muted ms-2';
            $reorderOf.textContent = i18n('reorderOfN').replace('%s', String(cartItem.order.invoiceNumber ?? ''));
            $serial.after($reorderOf);
        }
    } else if (order) {
        $serialCode.textContent = `#${order.invoiceNumber || ''}`;
        $serial.href = order.iapURL();
    } else {
        $serial.replaceWith();
    }

    ($el.querySelector('.cart-item-realm') as HTMLElement).textContent = quote?.realm?.name ?? order?.realm?.name ?? '';
    ($el.querySelector('.cart-item-product') as HTMLElement).textContent = parts.join(' – ');
    ($el.querySelector('.cart-item-qty') as HTMLElement).textContent = pieceCount(cartItem);
    ($el.querySelector('.cart-item-price') as HTMLElement).textContent = DollarFormat(cartItem.subtotal ?? 0);

    const proofs = quote?.quoteProofs ?? order?.orderProofs ?? [];
    const cloudFront = quote?.realm?.cloudFront || order?.realm?.cloudFront || window.CloudFront;
    const $proofs = $el.querySelector('.cart-item-proofs') as HTMLElement;
    const $proofsList = $el.querySelector('.cart-item-proofs-list') as HTMLElement;
    let proofCount = 0;
    for (const entry of proofs) {
        const file = entry.proof?.file;
        if (!file) {
            continue;
        }
        const urls = GetProofURLs(cloudFront, file);
        const label = proofLabel(entry);
        const $thumb = template.Clone('#cart-proof-template').firstElementChild as HTMLElement;
        const $link = $thumb.querySelector('a') as HTMLAnchorElement;
        $link.href = urls.main;
        $link.setAttribute('data-caption', label);
        const $img = $thumb.querySelector('.cart-proof-img') as HTMLImageElement;
        $img.alt = label;
        void Picture($img, urls.thumb, { displayWidth: 56, moderationLabels: entry.proof?.moderationLabels });
        ($thumb.querySelector('.cart-proof-label') as HTMLElement).textContent = label;
        $proofsList.appendChild($thumb);
        proofCount++;
    }
    if (proofCount > 0) {
        $proofs.classList.remove('d-none');
    }

    // Open-in-IAP / open-on-customer-site links with copy buttons — mirrors the
    // checkout summary. Only quote- and order-backed items expose these URLs;
    // the customer copy runs through the shortener, the IAP copy is verbatim.
    let iapURL = '';
    let customerURL = '';
    let realmName = '';
    let companyID = '';
    if (quote) {
        iapURL = quote.iapURL();
        customerURL = quote.customerURL();
        realmName = quote.realm?.name ?? '';
        companyID = quote.realm?.id ?? '';
    } else if (order) {
        iapURL = order.iapURL();
        customerURL = order.customerURL();
        realmName = order.realm?.name ?? '';
        companyID = order.realm?.id ?? '';
    }
    if (iapURL) {
        ($el.querySelector('.cart-item-links') as HTMLElement).classList.remove('d-none');

        const $iapLink = $el.querySelector('.cart-item-iap-link') as HTMLAnchorElement;
        $iapLink.href = iapURL;
        appendLinkLabel($iapLink, i18n('openInIap'));
        const $iapCopy = $el.querySelector('.cart-item-iap-copy') as HTMLButtonElement;
        $iapCopy.addEventListener('click', () => clipboard.Copy(iapURL, $iapCopy));

        if (customerURL) {
            const $customerLink = $el.querySelector('.cart-item-customer-link') as HTMLAnchorElement;
            $customerLink.href = customerURL;
            appendLinkLabel($customerLink, i18n('openInIap') + (realmName ? ` · ${realmName}` : ''));
            const $customerCopy = $el.querySelector('.cart-item-customer-copy') as HTMLButtonElement;
            $customerCopy.addEventListener('click', () => {
                void ShortURL(customerURL, companyID).then(short => clipboard.Copy(short, $customerCopy));
            });
        } else {
            ($el.querySelector('.cart-item-customer-row') as HTMLElement).remove();
        }
    }

    ($el.querySelector('.cart-item-breakdown') as HTMLButtonElement).addEventListener('click', () => {
        OpenPriceBreakdown(cartItem);
    });
    ($el.querySelector('.cart-item-remove') as HTMLButtonElement).addEventListener('click', () => {
        void removeItem(cartItem.id ?? '');
    });

    return $el;
}

async function removeItem(cartItemID: string) {
    if (!cartItemID) {
        return;
    }
    const oldCartID = cartID.Get();
    await DeleteCartItem(cartItemID).then(async resp => {
        // Removing from a saved cart clones it (201) so the saved copy is
        // preserved; switch to the clone so the removal is what we redraw.
        const newCartID = resp.data.cartID;
        const cloned = resp.status === 201 && Boolean(newCartID) && newCartID !== oldCartID;
        if (cloned && newCartID) {
            await SwitchCart(newCartID, undefined, false);
        }
        void drawCart();
        toast.Undo(i18n('itemRemoved'), () => {
            if (cloned && newCartID) {
                // The saved cart still has the item — switch back to it and drop
                // the clone the removal created so it doesn't linger orphaned.
                return SwitchCart(oldCartID, undefined, false).
                    then(() => DeleteCart(newCartID)).
                    then(() => void drawCart());
            } else {
                return RestoreCartItem(cartItemID).then(() => void drawCart());
            }
        }, { label: i18n('undo') });
    });
}

// Populate the "Switch cart" select with the user's saved (named) carts plus
// the active cart. Same query the cart modal uses for its switcher.
async function loadSwitcher(activeCart?: models.HyejooCart) {
    const namedCarts = await axios.get('/carts?q=user:me+activeOrOrdered:yes+has:name&data=id,name,active,ordered,activeOrOrdered').
        then(resp => models.CartsResponse.fromJSON(resp.data).carts).
        catch(() => []);

    $switcher.replaceChildren();

    const $placeholder = document.createElement('option');
    $placeholder.value = '';
    $placeholder.textContent = activeCart?.name || i18n('currentCart');
    $placeholder.selected = true;
    $switcher.appendChild($placeholder);

    for (const namedCart of namedCarts) {
        if (!namedCart.id || !namedCart.name || namedCart.ordered || namedCart.id === activeCart?.id) {
            continue;
        }
        const $opt = document.createElement('option');
        $opt.value = namedCart.id;
        $opt.textContent = namedCart.name;
        $switcher.appendChild($opt);
    }

    $switcher.disabled = $switcher.childElementCount <= 1;
}

// Guard before abandoning the active cart (New cart / Switch cart). An unsaved,
// non-empty cart has no obvious UI pointer once you leave it, so confirm —
// ported from the deleted modal's promptNewCartFromModal. Resolves true when
// it's safe to proceed (empty/already-named cart, the admin saved it, or chose
// "Don't save"), false when they cancel.
async function confirmLeaveCart(): Promise<boolean> {
    const cart = (await getCart())?.cart;
    if (!cart || cart.cartItems.length === 0 || cart.name) {
        return true;
    }
    return new Promise<boolean>(resolve => {
        // settled guard: a Modal close() fires onClose too, so resolve once and
        // ignore the trailing onClose(false) after a Save / Don't-save choice.
        let settled = false;
        const done = (proceed: boolean) => {
            if (!settled) {
                settled = true;
                resolve(proceed);
            }
        };
        const m = new Modal({
            title: i18n('unsavedCart'),
            body: `<div>${i18n('unsavedCartBody')}</div>`,
            input: 'text',
            inputPlaceholder: i18n('cartName'),
            confirmButtonClass: 'btn btn-primary',
            confirmText: i18n('saveAndContinue'),
            cancelButton: false,
            onSubmit: async () => {
                const name = (m.$element.querySelector('input') as HTMLInputElement).value.trim();
                if (name) {
                    // Don't proceed if the save fails — leaving the cart now would
                    // lose it despite the admin choosing to save. Keep the modal
                    // open so they can retry or cancel.
                    try {
                        await RenameCart(cart.id ?? '', name);
                    } catch (err) {
                        ShowError(err);
                        return;
                    }
                }
                done(true);
                m.close();
            },
            onClose: () => done(false),
            buttons: [{
                text: i18n('dontSave'),
                class: 'btn btn-outline-secondary',
                click: e => {
                    e.preventDefault();
                    done(true);
                    m.close();
                },
            }],
        });
    });
}

function promptSaveCart(cart: models.HyejooCart) {
    if (!cart.id) {
        return;
    }
    const m = new Modal({
        title: cart.name ? i18n('saveCartNamed').replace('%s', cart.name) : i18n('saveCart'),
        input: 'text',
        inputPlaceholder: i18n('cartName'),
        confirmButtonClass: 'btn btn-primary',
        confirmText: i18n('save'),
        onSubmit: async () => {
            const name = (m.$element.querySelector('input') as HTMLInputElement).value.trim();
            if (!name) {
                return;
            }
            await RenameCart(cart.id ?? '', name).then(() => {
                m.close();
                toast.Success(i18n('cartSaved'));
                void drawCart();
            }).catch(ShowError);
        },
    });
}

async function drawCart() {
    const resp = await getCart();
    $spinner.classList.add('d-none');

    if (!resp || resp.cart.cartItems.length === 0) {
        $content.classList.add('d-none');
        $empty.classList.remove('d-none');
        await loadSwitcher(resp?.cart);
        return;
    }

    const cart = resp.cart;
    $empty.classList.add('d-none');
    $heading.textContent = cart.name || i18n('navCart');

    const f = document.createDocumentFragment();
    for (const cartItem of cart.cartItems) {
        f.appendChild(renderItem(cartItem));
    }
    $items.replaceChildren(f);

    $subtotal.textContent = DollarFormat(resp.subtotal);
    $total.textContent = DollarFormat(resp.total);

    $saveBtn.onclick = () => promptSaveCart(cart);
    $shareBtn.onclick = () => void ShareCart(cart.id ?? '');

    $content.classList.remove('d-none');
    await loadSwitcher(cart);
}

$switcher.addEventListener('change', async () => {
    const toCartID = $switcher.value;
    if (!toCartID) {
        return;
    }
    const okToSwitch = await confirmLeaveCart();
    if (!okToSwitch) {
        $switcher.selectedIndex = 0;
        return;
    }
    await SwitchCart(toCartID, cartID.Get()).then(() => void drawCart());
});

$newBtn.addEventListener('click', async () => {
    const okToReset = await confirmLeaveCart();
    if (!okToReset) {
        return;
    }
    await cartID.Forget();
    void drawCart();
});

function addItems() {
    // GetCart (not the page's getCart) returns the hyejoo cart the add-items
    // prompt expects; the prompt only reads its id to decide new-vs-existing.
    // Redraw via onComplete (fires after any duplicate-cart switch settles), not
    // the prompt promise — that resolves/closes before the switch lands.
    void GetCart().then(resp => {
        void PromptAddCartItemsToCart(resp?.cart, undefined, () => void drawCart()).catch(() => undefined);
    });
}

$addBtn.addEventListener('click', addItems);
$emptyAddBtn.addEventListener('click', addItems);

async function init() {
    if (!await HasPerm('r/carts')) {
        window.location.replace(Relref('/'));
        return;
    }

    // Deep-link adds (quoteID/orderID/quote/invoiceNumber add to the cart, cart
    // merges another cart in) before the first paint. When they fire, the redraw
    // callback renders (and keeps the page in sync through toast undo/redo);
    // otherwise we draw the existing cart ourselves.
    const added = await ApplyAddParamsFromQuery(false, () => void drawCart());
    if (!added) {
        await drawCart();
    }
}

void init();
